import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

/**
 * Convert flat array → hierarchical object
 * Main → Group → Sub-Group → Ledger
 */
const convertToHierarchy = (data) => {
  const output = {};

  data.forEach((row) => {
    if (row.type === "main") {
      if (!output[row.label]) output[row.label] = {};
    }

    if (row.type === "group") {
      if (!output[row.mainKey]) output[row.mainKey] = {};
      if (!output[row.mainKey][row.label]) output[row.mainKey][row.label] = [];
    }

    if (row.type === "sub") {
      const parts = row.groupKey.split("-");
      const main = parts[0];
      const group = parts[1];
      output[main][group].push({
        isSubHeader: true,
        label: row.label
      });
    }

    if (row.type === "ledger") {
      const parts = row.parentKey.split("-");
      const main = parts[0];
      const group = parts[1];
      const sub = parts[2] || null;

      const targetArray = sub
        ? output[main][group].find(s => s.isSubHeader && s.label === sub)
            .ledgers || (output[main][group].find(s => s.isSubHeader && s.label === sub).ledgers = [])
        : output[main][group];

      targetArray.push({
        acct_name: row.acct_name,
        status: row.status
      });
    }
  });

  return output;
};

export const exportCAExcel = ({ groupedData, societyName }) => {
  const wb = XLSX.utils.book_new();
  const wsData = [];

  const structuredData = convertToHierarchy(groupedData);

  // ------------------------------
  // 1. Report header
  // ------------------------------
  wsData.push([societyName]);
  wsData.push([]);
  wsData.push(["Chart Of Accounts"]);
  wsData.push([]);
  
  // ------------------------------
  // 2. Column headers
  // ------------------------------
  wsData.push(["Description", "Account Status"]);

  // ------------------------------
  // 3. Build hierarchy rows
  // ------------------------------
  Object.keys(structuredData).forEach((mainGrp) => {
    wsData.push([mainGrp]); // Main Group

    const groups = structuredData[mainGrp];
    Object.keys(groups).forEach((grp) => {
      const items = groups[grp];

      // First row is always group header
      wsData.push([`   ➤ ${grp}`, ""]);

      items.forEach((item) => {
        if (item.isSubHeader) {
          wsData.push([`        - ${item.label}`, ""]);
          if (item.ledgers) {
            item.ledgers.forEach((ledger) => {
              wsData.push([`            • ${ledger.acct_name}`, ledger.status]);
            });
          }
        } else {
          // ledger without sub-group
          wsData.push([`        • ${item.acct_name}`, item.status]);
        }
      });

      wsData.push([]); // spacing after group
    });

    wsData.push([]); // spacing after main group
  });

  // ------------------------------
  // 4. Create worksheet
  // ------------------------------
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const range = XLSX.utils.decode_range(ws["!ref"]);
  if (!ws["!merges"]) ws["!merges"] = [];

  // ------------------------------
  // 5. Formatting
  // ------------------------------
  for (let R = range.s.r; R <= range.e.r; ++R) {
    const row = wsData[R];
    for (let C = 0; C < 2; C++) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellRef];
      if (!cell) continue;

      // Big headers
      if (R === 0 || R === 2) {
        cell.s = { font: { bold: true, sz: 14 }, alignment: { horizontal: "center" } };
        ws["!merges"].push({ s: { r: R, c: 0 }, e: { r: R, c: 1 } });
        continue;
      }

      // Column header
      if (R === 4) {
        cell.s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "DDDDDD" } },
          alignment: { horizontal: "center" }
        };
        continue;
      }

      // Main Group
      if (row.length === 1 && R > 4) {
        cell.s = {
          font: { bold: true, sz: 12 },
          fill: { fgColor: { rgb: "CFE0FF" } }
        };
        ws["!merges"].push({ s: { r: R, c: 0 }, e: { r: R, c: 1 } });
        continue;
      }

      // Group row (➤)
      if (typeof row[0] === "string" && row[0].trim().startsWith("➤")) {
        cell.s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "E7F0FF" } },
          alignment: { horizontal: "left" }
        };
        continue;
      }

      // Sub-group row (-)
      if (typeof row[0] === "string" && row[0].includes("-")) {
        cell.s = { 
          font: { bold: true },
          fill: { fgColor: { rgb: "D3D3D3" } },
          alignment: { horizontal: "left" } };
        continue;
      }

      // Ledger row (•)
      if (typeof row[0] === "string" && row[0].includes("•")) {
        cell.s = { alignment: { horizontal: "left" } };
        continue;
      }
    }
  }

  // Column widths
  ws["!cols"] = [{ wch: 50 }, { wch: 20 }];

  XLSX.utils.book_append_sheet(wb, ws, "Chart_Of_Accounts");
  const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  saveAs(new Blob([wbout], { type: "application/octet-stream" }), `Chart_Of_Accounts.xlsx`);
};
